import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PrimaryControlsPane from './PrimaryControlsPane';
import { PRINT_PRODUCT_CONFIGURATOR_PRIMARY_CONTROL } from './PrimaryControlsPane/PrimaryControlsPane';
import SecondaryControlButtonSizeRectangle from './SecondaryControlButton/SecondaryControlButtonSizeRectangle';
import SecondaryControlButton from './SecondaryControlButton';
import CardTemplatePreview from '../../../../../cards/components/CardTemplatePreview';
import { getCardTemplateSizing } from '../../../../../cards/components/AccountCard/helpers';
import PriceDisplay from '../../../../../../components/global/PriceDisplay';
import Badge from '../../../../../../components/global/Badge';
import DesignEditorControls, {
  DesignEditorSecondaryControls,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorControls';
import { getPrintSizeTitle } from '../../../../../../components/global/CardDesigner/helpers';

const propTypes = {
  className: PropTypes.string,
  printProduct: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sizes: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          print_size: PropTypes.shape({
            id: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
          }).isRequired,
          mounts: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                print_mount: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string.isRequired,
                }).isRequired,
              }).isRequired,
            ).isRequired,
          }).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
  selectedPrintProductSize: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  availablePrintProductSizeIds: PropTypes.arrayOf(PropTypes.string),
  selectedPrintProductMount: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  selectedCardSide: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  handlePrintProductSizeSelect: PropTypes.func.isRequired,
  handlePrintProductMountSelect: PropTypes.func.isRequired,
  handleCardSideSelect: PropTypes.func.isRequired,
};

const PrintProductConfiguratorControlsPane = ({
  className,
  printProduct,
  cardSides,
  selectedPrintProductSize,
  availablePrintProductSizeIds,
  handlePrintProductSizeSelect,
  selectedPrintProductMount,
  handlePrintProductMountSelect,
  selectedCardSide,
  handleCardSideSelect,
}) => {
  const classes = classNames({
    [className]: !!className,
  });
  const [activeControl, setActiveControl] = useState(
    PRINT_PRODUCT_CONFIGURATOR_PRIMARY_CONTROL.SIZE,
  );

  const printProductSizes = (printProduct.sizes?.records ?? []).filter(
    printProductSize =>
      availablePrintProductSizeIds.length === 0 ||
      availablePrintProductSizeIds.includes(printProductSize.id),
  );
  const printProductMounts = selectedPrintProductSize?.mounts;

  return (
    <DesignEditorControls className={classes}>
      <PrimaryControlsPane
        printProduct={printProduct}
        activeControl={activeControl}
        onClick={control => setActiveControl(control)}
        supportsMounts={printProductMounts?.records?.length > 0}
        // CardSide selection makes sense only when you have more than 1 to choose from
        // the first one is automatically applied anyway.
        supportsCardSides={cardSides?.length > 1}
      />

      {activeControl === PRINT_PRODUCT_CONFIGURATOR_PRIMARY_CONTROL.SIZE && (
        <DesignEditorSecondaryControls className="print-product-configurator-controls-secondary">
          {printProductSizes.map(printProductSize => {
            const { print_size: printSize } = printProductSize;
            return printProductSize.orientations.map(orientation => {
              const { width, height } = getCardTemplateSizing({
                orientation,
                print_size: {
                  width: printSize.width,
                  height: printSize.height,
                },
              });

              const title = getPrintSizeTitle({ printSize, width, height });

              return (
                <SecondaryControlButton
                  key={`${printProductSize.id}-${orientation}`}
                  title={title}
                  icon={
                    <SecondaryControlButtonSizeRectangle
                      width={width}
                      height={height}
                      orientation={orientation}
                    />
                  }
                  onClick={() => {
                    handlePrintProductSizeSelect(
                      printProductSize.id,
                      orientation,
                    );
                  }}
                  active={
                    printProductSize.id === selectedPrintProductSize?.id &&
                    orientation === selectedPrintProductSize.orientation
                  }
                />
              );
            });
          })}
        </DesignEditorSecondaryControls>
      )}

      {activeControl ===
        PRINT_PRODUCT_CONFIGURATOR_PRIMARY_CONTROL.MOUNTING && (
        <DesignEditorSecondaryControls className="print-product-configurator-controls-secondary">
          {printProductMounts?.records?.length > 0 &&
            printProductMounts?.records.map(printProductMount => {
              return (
                <SecondaryControlButton
                  key={printProductMount.print_mount.id}
                  title={printProductMount.print_mount.name}
                  icon={
                    <Fragment>
                      <SecondaryControlButtonSizeRectangle
                        className="is-mount"
                        width={
                          printProductMount.print_product_size.print_size.width
                        }
                        height={
                          printProductMount.print_product_size.print_size.height
                        }
                      />
                      {printProductMount.price?.value > 0 && (
                        <Badge badgeSize="sm" badgeStyle="yellow">
                          <PriceDisplay
                            price={printProductMount.price.value}
                            currency={printProductMount.price.currency}
                          />
                        </Badge>
                      )}
                    </Fragment>
                  }
                  onClick={() => {
                    handlePrintProductMountSelect(printProductMount.id);
                  }}
                  active={
                    printProductMount.id === selectedPrintProductMount?.id
                  }
                />
              );
            })}
        </DesignEditorSecondaryControls>
      )}

      {activeControl ===
        PRINT_PRODUCT_CONFIGURATOR_PRIMARY_CONTROL.CARD_SIDES && (
        <DesignEditorSecondaryControls className="print-product-configurator-controls-secondary">
          {cardSides.map(cardSide => {
            const cardTemplate = cardSide.card_template;

            return (
              <SecondaryControlButton
                key={cardSide.id}
                title={cardSide.name}
                icon={<CardTemplatePreview cardTemplate={cardTemplate} />}
                onClick={() => {
                  handleCardSideSelect(cardSide.id);
                }}
                active={cardSide.id === selectedCardSide?.id}
              />
            );
          })}
        </DesignEditorSecondaryControls>
      )}
    </DesignEditorControls>
  );
};

PrintProductConfiguratorControlsPane.propTypes = propTypes;

export default PrintProductConfiguratorControlsPane;
